/**
 * Query-key factory and PEOPLE types of the academy (F3-D) + the few Drizzle
 * query builders that REMAIN local-first.
 *
 * **Plan 06 · E4:** the directory, a student's record, a teacher's profile
 * and the academy's guardian links are served over REST. Their rows stopped
 * being replicated (`org_admin_scope`, `campus_admin_scope` and
 * `academy_org` no longer carry them), so there is no —and can be no—
 * equivalent local query here: it would be a path to data the device doesn't
 * have.
 *
 * What remains local-first, and why: **my** enrollments and **my**
 * certificates (`my_cohorts_student`, `my_certificates`) and **my** own
 * record / profile / guardianship (`my_membership_profiles`). They are mine,
 * small and filtered by `auth.user_id()` — the criterion in
 * `DATA_ACCESS_POLICY.md` §2 for staying in sync.
 */

import type { components } from "@space/client";
import {
  and,
  certificate,
  cohort,
  cohort_enrollment,
  desc,
  eq,
  guardian_link,
  isNull,
  type PowerSyncDrizzleDB,
  student_profile,
  teacher_profile,
} from "@space/powersync";

/** Cache keys of the people layer (TanStack Query). */
export const academyPeopleQueryKeys = {
  all: ["academy-people"] as const,
  /**
   * Academy directory. **Plan 06 · E4:** a single key once again — the
   * `directoryRest` that coexisted with the local one during the flag
   * cutover no longer has anything to coexist with: the Drizzle query has
   * been deleted and the only source is `GET /api/academy/:orgId/directory`.
   */
  directory: (orgId: string | undefined) => ["academy-people", "directory", orgId] as const,
  studentProfile: (orgId: string | undefined, userId: string | undefined) =>
    ["academy-people", "student-profile", orgId, userId] as const,
  teacherProfile: (orgId: string | undefined, userId: string | undefined) =>
    ["academy-people", "teacher-profile", orgId, userId] as const,
  /**
   * Org identities. SINGLE source and the only possible source: the local
   * mirror of `"user"` disappeared with the stream trimming (D3), so there
   * is no longer a "mode" to pick.
   */
  identities: (orgId: string | undefined) => ["academy-people", "identities", orgId] as const,
  enrollments: (userId: string | undefined) => ["academy-people", "enrollments", userId] as const,
  certificates: (userId: string | undefined) => ["academy-people", "certificates", userId] as const,
  guardianLinks: (orgId: string | undefined) => ["academy-people", "guardians", orgId] as const,
  myStudentProfile: (userId: string | undefined) =>
    ["academy-people", "me", "student", userId] as const,
  myTeacherProfile: (userId: string | undefined) =>
    ["academy-people", "me", "teacher", userId] as const,
  myGuardianLinks: (userId: string | undefined) =>
    ["academy-people", "me", "guardians", userId] as const,
  myRole: (orgId: string | undefined, userId: string | undefined) =>
    ["academy-people", "my-role", orgId, userId] as const,
  orgRow: (orgId: string | undefined) => ["academy-people", "org-row", orgId] as const,
  publicTeacher: (orgSlug: string, slug: string) =>
    ["academy-people", "public-teacher", orgSlug, slug] as const,
  /**
   * P41 · what the STUDENT knows about their own guardianship
   * (`GET /api/me/guardianship?org=…`). Goes over REST and NOT sync because
   * the response composes three things that no replicated table contains
   * together: the consent status of each link, what a guardian gets to see
   * (derived from the actual streams) and the **age threshold in force at
   * that academy**, which lives in `app_config` and doesn't sync.
   */
  myGuardianship: (orgId: string | undefined) =>
    ["academy-people", "me", "guardianship", orgId] as const,
  /**
   * P41 · the consent request resolved from its token
   * (`GET /api/public/guardian-consent?token=…`). The token IS the
   * credential and that's why it goes into the key: two different links are
   * two different queries, and neither should reuse the other's cache.
   */
  guardianConsentRequest: (token: string) => ["academy-people", "guardian-consent", token] as const,
};

// ─── Types that come from the server (SINGLE source: the Rust OpenAPI) ───────
//
// E4 · plan 06: these four shapes are DERIVED from the server's contract
// instead of being inferred from Drizzle's `select()`. Not cosmetic — while
// the row came from the local mirror, the type was fixed by the SQLite
// schema (booleans as 0/1, `jsonb` as a string) and had to be translated in
// every consumer. Now there is one type per piece of data and the DTO rules.

/**
 * Directory row WITHOUT identity: membership + summarized record.
 * Served by `GET /api/academy/:orgId/directory`, which scopes by campus for
 * `campus_admin` — a scope the sync rules don't express.
 */
export type DirectoryMemberRow = components["schemas"]["OrgDirectoryEntryDTO"];

/** A student's full record (`GET /api/academy/:orgId/students/:userId`). */
export type StudentRecord = components["schemas"]["StudentRecordDTO"];

/** SPARSE record patch: what doesn't travel doesn't get touched. */
export type StudentRecordPatch = components["schemas"]["StudentRecordPatchDTO"];

/** Teacher profile (`GET /api/academy/:orgId/teachers/:userId`). */
export type TeacherRecord = components["schemas"]["TeacherRecordDTO"];

/** SPARSE teacher profile patch. */
export type TeacherRecordPatch = components["schemas"]["TeacherRecordPatchDTO"];

/** Public teacher card for a cohort (`GET /api/cohorts/:id/teachers`). */
export type CohortTeacherCard = components["schemas"]["CohortTeacherCardDTO"];

/** Guardian↔student link as served by `GET /api/org/listGuardians`. */
export type GuardianLinkRow = components["schemas"]["GuardianLinkDTO"];

/**
 * P41 · what the student knows about their own guardianship
 * (`GET /api/me/guardianship?org=…`).
 *
 * `consentAgeYears` is the threshold **resolved for that academy**. It's the
 * only place the client can know it from: there is no replicated table with
 * it and `org_settings` (which does sync) doesn't carry it. Any screen that
 * needs the threshold reads it from here — hardcoding it would disconnect it
 * from `app_config`, which is exactly what this campaign exists to prevent.
 */
export type MyGuardianship = components["schemas"]["MyGuardianship"];

/**
 * P41 · a live consent request, resolved from its token
 * (`GET /api/public/guardian-consent?token=…`). The guardian views it
 * WITHOUT a session: the credential is the link's token.
 */
export type GuardianConsentRequest = components["schemas"]["RequestView"];

/**
 * Identity of a person within an organization. **Single source** on the
 * client: served by `useOrgIdentities`, never a loose join per screen.
 *
 * `email === null` doesn't mean "has no email": it means **redacted** —
 * whoever is asking doesn't have `ManageMembers` or `ViewCampusData` in the
 * org (`org_rbac::can_view_member_emails`).
 */
export interface OrgIdentity {
  userId: string;
  role: string | null;
  campusId: string | null;
  name: string | null;
  email: string | null;
  image: string | null;
}

/** Directory row already hydrated with identity (what the selectors see). */
export type DirectoryRow = DirectoryMemberRow & Pick<OrgIdentity, "name" | "email" | "image">;

// E4 · DELETED: `getOrgDirectoryQuery`, `getStudentProfileQuery`,
// `getTeacherProfileQuery` and `getOrgGuardianLinksQuery` used to live here —
// the four Drizzle queries reading THIRD-PARTY records, profiles and
// guardianships off the local mirror. They died with the trimming of
// `org_admin_scope` / `campus_admin_scope`: without those replicated rows
// they'd return empty for everyone, and leaving them "just in case" is exactly the second path the policy forbids.
// They're replaced by `GET /api/academy/:orgId/{directory,students/:id,teachers/:id}`
// and `GET /api/org/listGuardians`, where campus scope and capability are
// decided at the ONE place with authority: the server.
//
// (`getOrgIdentitiesQuery` had already left in E2, for the same reason
//  applied to the `"user"` table.)

/** A user's enrollments (joined with the cohort) — most recent first. */
export function getUserEnrollmentsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: cohort_enrollment.id,
      cohortId: cohort_enrollment.cohort_id,
      organizationId: cohort_enrollment.organization_id,
      status: cohort_enrollment.status,
      finalGrade: cohort_enrollment.final_grade,
      finalGradeScheme: cohort_enrollment.final_grade_scheme,
      enrolledAt: cohort_enrollment.enrolled_at,
      completedAt: cohort_enrollment.completed_at,
      cohortName: cohort.name,
      cohortCode: cohort.code,
      courseId: cohort.course_id,
      // Attendance stats (columns added by F3-A). The SELECT brings them if
      // they exist; the consumer renders them defensively (they can be NULL
      // before F3-A lands). Accessed via `attendanceStatsOf`.
      raw: cohort_enrollment,
    })
    .from(cohort_enrollment)
    .leftJoin(cohort, eq(cohort.id, cohort_enrollment.cohort_id))
    .where(and(eq(cohort_enrollment.user_id, userId), isNull(cohort_enrollment.deleted_at)))
    .orderBy(desc(cohort_enrollment.enrolled_at));
}

/** A user's certificates (local-first; empty if not synced). */
export function getUserCertificatesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(certificate)
    .where(eq(certificate.user_id, userId))
    .orderBy(desc(certificate.issued_at));
}

// ─── self-view (student / guardian) ───────────────────────────────────────────

/** MY student record (my_membership_profiles). */
export function getMyStudentProfileQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(student_profile)
    .where(and(eq(student_profile.user_id, userId), isNull(student_profile.deleted_at)));
}

/** MY teacher profile (my_membership_profiles). */
export function getMyTeacherProfileQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(teacher_profile).where(eq(teacher_profile.user_id, userId)).limit(1);
}

/** MY links as a guardian (I am the guardian of N students). */
export function getMyGuardianWardsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(guardian_link).where(eq(guardian_link.guardian_user_id, userId));
}
